import { useUserPhotoQuery } from "@/common/api/user/use-user-photo.query";
import { getInitials } from "@/common/helpers/get-initials";
import { DEACTIVATED_USER_AVATAR_CLASS_NAME } from "@/common/helpers/user-deactivated";
import { cn } from "@/common/helpers/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { AvatarLightboxDialog } from "@/components/user-avatar/avatar-lightbox-dialog";
import { useTranslation } from "react-i18next";

export enum AvatarClickAction {
  None = "None",
  Zoom = "Zoom",
}

export enum UserAvatarSize {
  Xs = "Xs",
  Sm = "Sm",
  Md = "Md",
  Lg = "Lg",
  Xl = "Xl",
}

const USER_AVATAR_SIZE_CLASS_NAMES: Record<UserAvatarSize, string> = {
  [UserAvatarSize.Xs]: "size-6 text-[10px]",
  [UserAvatarSize.Sm]: "size-8 text-xs",
  [UserAvatarSize.Md]: "size-10",
  [UserAvatarSize.Lg]: "size-12",
  [UserAvatarSize.Xl]: "size-14",
};

export interface UserAvatarProps {
  userAzureObjectUuid?: string | null;
  name?: string;
  clickAction?: AvatarClickAction;
  size?: UserAvatarSize;
  isDeactivated?: boolean;
  className?: string;
}
export const UserAvatar = ({
  userAzureObjectUuid,
  name,
  clickAction = AvatarClickAction.None,
  size = UserAvatarSize.Md,
  isDeactivated = false,
  className,
}: UserAvatarProps) => {
  const { t } = useTranslation("common");
  const { data, isLoading } = useUserPhotoQuery({ userAzureObjectUuid });

  const altText = name ? t("ImageOf", { name }) : t("UserImage");

  const avatar = (
    <Avatar
      className={cn(USER_AVATAR_SIZE_CLASS_NAMES[size], isDeactivated && DEACTIVATED_USER_AVATAR_CLASS_NAME, className)}
    >
      {data ? (
        <AvatarImage src={data} alt={altText} />
      ) : isLoading ? (
        <AvatarFallback>
          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
        </AvatarFallback>
      ) : (
        <AvatarFallback>{name ? getInitials(name) : "?"}</AvatarFallback>
      )}
    </Avatar>
  );

  if (clickAction === AvatarClickAction.Zoom && userAzureObjectUuid) {
    return <AvatarLightboxDialog userAzureObjectUuid={userAzureObjectUuid} alt={altText} trigger={avatar} />;
  }

  return avatar;
};
